import { useMutation, useQueryClient } from '@tanstack/react-query'
import { DeliverTxResponse } from '@cosmjs/stargate'
import { TxRaw } from 'cosmjs-types/cosmos/tx/v1beta1/tx'
import { assert } from '@/utils'
import { queryKeys } from '@/query-keys'
import { assertIsDeliverTxSuccess, IBCTransferStatus, broadcastTx, traceIbcStatus2 } from '@/wallet-utils'
import { MutationParameters } from './types'
import { useStrideChain } from '@/contexts/wallet'
import { flushSync } from 'react-dom'

export type BroadcastSendTokenMutationMode = 'deposit' | 'withdraw'

export interface BroadcastSendTokenMutationReturnType {
  transaction: DeliverTxResponse
  status: IBCTransferStatus
}

const useBroadcastWithdrawMutation = ({ withdrawRaw, setWithdrawTransaction }: MutationParameters) => {
  const { getSigningStargateClient } = useStrideChain()

  const queryClient = useQueryClient()

  const handleMutation = async (): Promise<BroadcastSendTokenMutationReturnType> => {
    assert(withdrawRaw, 'Unable to broadcast transaction without a signed transaction raw.')

    const bytes = TxRaw.encode(withdrawRaw).finish()

    const client = await getSigningStargateClient()

    const transaction = await broadcastTx(client, bytes)

    assertIsDeliverTxSuccess(transaction)

    const status = await traceIbcStatus2({ denom: 'STRD', tx: transaction })

    if (status === 'return-later') {
      return {
        transaction: transaction,
        status: 'return-later'
      }
    }

    // We'll handle this after balance is updated because gas fee is still consumed
    // @TODO: It's possible that we may have to wait for funds to be fully refunded
    // @TODO: Handle error - we probably need a flow for this
    if (status === 'timeout') {
      return {
        transaction: transaction,
        status: 'timeout'
      }
    }

    return {
      transaction: transaction,
      status: 'complete'
    }
  }

  const handleSuccess = async ({ transaction }: BroadcastSendTokenMutationReturnType) => {
    await Promise.all([
      queryClient.invalidateQueries({ queryKey: queryKeys.chainBalancesBase }),
      queryClient.invalidateQueries({ queryKey: queryKeys.transactionHistoryBase }),
      queryClient.invalidateQueries({
        queryKey: queryKeys.transactionHistoryIbcStatusByHash({ hash: transaction.transactionHash })
      })
    ])

    // Allow updates to propagate so trace function has access to this when it's ran asynchronously
    flushSync(() => {
      setWithdrawTransaction(transaction)
    })
  }

  return useMutation({
    mutationFn: handleMutation,
    onSuccess: handleSuccess
  })
}

export { useBroadcastWithdrawMutation }
